import { Button } from "@/components/ui/baseUi/button"
import { UserCell } from "@/components/ui/layout/table/userCell"
import { Separator } from "@/components/ui/baseUi/separator"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { Toggle } from "@/components/ui/baseUi/toggle"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { cn } from "@/modules/lib/utils"
import { useUser } from "@/modules/users/hooks/useUser"
import { shortenText } from "@/modules/utils/stringUtils"
import backgroundBenchmark from "@images/backgroundBenchmark.png"
import InvitationIcon from "@images/invitationIcon.svg"
import { ClockOutlineIcon } from "@images/svgIcons/clockOutlineIcon"
import { SwitchDeviceIcon } from "@images/svgIcons/illustrations/switchDeviceIcon"
import { CorrectIcon } from "@images/svgIcons/status/correctIcon"
import { ErrorIcon } from "@images/svgIcons/status/errorIcon"
import { AppleAppStoreIcon } from "@images/svgIcons/store/appleAppStore"
import { GooglePlayStoreIcon } from "@images/svgIcons/store/googlePlayStore"
import { UnionIcon } from "@images/svgIcons/unionIcon"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { useBox } from "@modules/box/hooks/useBox"
import { BoxOfferFormattedPrice } from "@modules/boxOffer/components/boxOfferFormSection"
import {
  type BoxOfferInvitationLink,
  BoxOfferInvitationStatus,
  BoxOfferType,
} from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOfferByUser } from "@modules/boxOffer/hooks/useBoxOffer"
import { useBoxOfferInvitationByUser, useBoxOfferSubscription } from "@modules/boxOffer/hooks/useBoxOfferSubscription"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PriceObjectEntity } from "@modules/common/priceObject/entities/priceObjectEntity"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { FeatureKeys } from "@modules/featureToggle/featureToggleTypes"
import { useFeatureToggle } from "@modules/featureToggle/hooks/useFeatureToggle"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { MemberOfferInvitationError } from "@modules/members/memberOffers/components/memberOfferInvitationError"
import { MemberOfferInvitationLoading } from "@modules/members/memberOffers/components/memberOfferInvitationLoading"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LocalStorageKeys } from "@modules/storage/localStorageKeys"
import dayjs from "dayjs"
import { Loader2, Tag } from "lucide-react"
import { QRCodeSVG } from "qrcode.react"
import { Link } from "react-router"

type Props = {
  boxOfferInvitationLink: BoxOfferInvitationLink
  className?: string
} & React.ComponentProps<"div">

export function MemberOfferInvitation({ boxOfferInvitationLink, className, ...props }: Props) {
  const canSubscribeToOffers = useFeatureToggle(FeatureKeys.BoxOffersByUser)
  const user = useUser()
  const language = useLanguage()
  const { data: boxOffer, isLoading: boxOfferLoading } = useBoxOfferByUser(
    boxOfferInvitationLink.boxId,
    boxOfferInvitationLink.boxOfferId,
  )
  const { result: box, isLoading: boxLoading } = useBox(boxOfferInvitationLink.boxId)
  const { hideSidebar } = useSidebar()
  const { refuseOfferInvitation } = useBoxOfferInvitationByUser()
  const { subscribeToOffer, isProcessing } = useBoxOfferSubscription()

  const priceOfferObject =
    boxOffer?.priceObject && boxOffer.invitation?.coupon
      ? PriceObjectEntity.fromDto({
          value: boxOffer.invitation.getPriceAfterDicount(boxOffer.priceObject.value),
          currency: boxOffer.priceObject.currency,
        })
      : boxOffer?.priceObject
  const isLoading = boxOfferLoading || boxLoading

  if (isLoading) return <MemberOfferInvitationLoading />
  if (
    !user ||
    !boxOffer ||
    !box ||
    !boxOffer.invitation ||
    user.id !== boxOffer.invitation.invitee.id ||
    boxOffer.invitation.status === BoxOfferInvitationStatus.CANCELLED ||
    boxOffer.invitation.status === BoxOfferInvitationStatus.REFUSED
  )
    return <MemberOfferInvitationError />

  return (
    <div className={cn("flex flex-col", className)} {...props}>
      {/* HEADER */}
      <div className="relative flex h-60 overflow-hidden rounded-b-3xl bg-neutral-200">
        <div className="mt-2 ml-4 flex h-full w-1/2 flex-col items-center justify-center gap-2">
          <div
            className="bg-contain bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${InvitationIcon} ')`, width: "60px", height: "60px" }}
          ></div>
          <Text fontStyle="HM" className="mt-1 line-clamp-1 text-textDefault-heading">
            {box.name}
          </Text>
          <Text fontStyle="BS" className="mt-2 text-textDefault-body">
            {word("member.offer.invitation.header.invitationBy")}
          </Text>
          {boxOffer.invitation.inviter.firstname &&
          boxOffer.invitation.inviter.lastname &&
          `${boxOffer.invitation.inviter.firstname} ${boxOffer.invitation.inviter.lastname}`.length > 21 ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <div className="w-full">
                    <UserCell
                      member={boxOffer.invitation.inviter}
                      subtitle={"@" + boxOffer.invitation.inviter.username}
                      className="mt-1 flex w-full gap-2"
                    />
                  </div>
                }
              />
              <TooltipContent side="bottom" className="max-w-50">
                <div className="flex flex-col gap-1">
                  <Text fontStyle="LL" className="wrap-break-word">
                    {boxOffer.invitation.inviter.firstname} {boxOffer.invitation.inviter.lastname}
                  </Text>
                  <Text fontStyle="LS" className="wrap-break-word">
                    {"@" + boxOffer.invitation.inviter.username}
                  </Text>
                </div>
              </TooltipContent>
            </Tooltip>
          ) : (
            <UserCell member={boxOffer.invitation.inviter} subtitle={"@" + boxOffer.invitation.inviter.username} />
          )}
        </div>
        <div
          className="h-full w-1/2 bg-cover bg-right bg-no-repeat"
          style={{
            background: `url('${backgroundBenchmark}')`,
            clipPath: "polygon(43% 0, 100% 0, 100% 100%, 0% 100%)",
            WebkitClipPath: "polygon(43% 0, 100% 0, 100% 100%, 0% 100%)",
          }}
        ></div>
      </div>

      {/* CONTENT */}
      {box && boxOffer && (
        <div className="flex flex-col gap-6 px-5">
          {/* Text content */}
          <div className="mt-8 flex w-full flex-col items-center justify-center text-center">
            <Text fontStyle="TL" className="text-textDefault-caption">
              {word("member.offer.invitation.subHeader.content", {
                box: box.name ?? "",
              })}
            </Text>
          </div>

          {/* Card offer content */}
          <div className="relative flex flex-col gap-2 overflow-hidden rounded-4xl bg-neutral-100 p-6">
            <Text fontStyle="LM" className="flex text-textDefault-body">
              {word(`box.offer.form.types.${boxOffer.type}`)}
            </Text>
            <Text fontStyle="HM" className="flex wrap-anywhere text-textDefault-body">
              {boxOffer.name}
            </Text>
            <div className="flex gap-2">
              <BoxOfferFormattedPrice
                wholePartStyle="DM"
                decimalPartStyle="TL"
                price={priceOfferObject ?? null}
                showRecurringPeriod={false}
                className="inline-flex items-end"
              />
              <div className="flex flex-col justify-end gap-1">
                {boxOffer.priceObject !== null && boxOffer.invitation.coupon && (
                  <Text fontStyle="LM" className="text-textDisabled line-through">
                    {formatCurrency(boxOffer.priceObject.value, boxOffer.priceObject.currency, language)}
                  </Text>
                )}
                <Text fontStyle="LL">{boxOffer.getBoxOfferTypeWord()}</Text>
              </div>
            </div>
            {/* Card offer Discount */}
            {boxOffer.priceObject !== null && boxOffer.invitation.coupon && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1">
                  <Tag className="text-textDefault-caption" size={13} />
                  <Text fontStyle="LS" className="flex text-textDefault-caption">
                    {word("box.member.table.offer.item.discount.label")}
                  </Text>
                </div>

                <div className="flex items-center gap-1">
                  <Text fontStyle="LS" className="flex text-textDefault-caption">
                    {boxOffer.invitation.coupon.type === CouponType.PERCENT
                      ? `-${boxOffer.invitation.coupon.value}%`
                      : `-${formatCurrency(boxOffer.invitation.coupon.value, boxOffer.priceObject.currency, language)}`}
                  </Text>
                  <Text fontStyle="LS" className="flex text-textDefault-caption">
                    ·
                  </Text>
                  <Text fontStyle="LS" className="flex text-textDefault-caption">
                    {boxOffer.invitation.coupon.durationInMonths
                      ? `${boxOffer.invitation.coupon.durationInMonths}
                    ${word("global.month.label", { count: boxOffer.invitation.coupon.durationInMonths ?? 0 })}`
                      : word("box.member.table.offer.discount.unlimitedInputLabel")}
                  </Text>
                </div>
              </div>
            )}
            <UnionIcon
              color="currentColor"
              className="absolute top-0 -right-11 h-full bg-contain bg-center bg-no-repeat text-neutral-400"
            />
          </div>

          {/* Device change content */}
          {canSubscribeToOffers ? (
            <div className="mt-6 flex w-full flex-row justify-between gap-4">
              <Button
                variant="outline"
                size="md"
                className="flex-1"
                disabled={refuseOfferInvitation.isPending}
                onClick={() =>
                  boxOffer.invitation && refuseOfferInvitation.mutate({ boxOfferInvitationId: boxOffer.invitation.id })
                }
              >
                {word("invitationDetailsInfo.refuse")}
                {refuseOfferInvitation.isPending ? (
                  <Loader2 color="currentColor" className="animate-spin" width={20} height={20} />
                ) : (
                  <ErrorIcon color="currentColor" className="text-white" width={20} height={20} />
                )}
              </Button>
              <Button
                variant="outline"
                size="md"
                className="flex-1"
                onClick={() => {
                  hideSidebar()
                  localStorage.removeItem(LocalStorageKeys.boxOfferInvitation.invitation)
                }}
                disabled={isProcessing}
              >
                {word("invitationDetailsInfo.answerLater")}
                {isProcessing ? (
                  <Loader2 color="currentColor" className="animate-spin" width={20} height={20} />
                ) : (
                  <ClockOutlineIcon color="currentColor" width={14} height={14} />
                )}
              </Button>
              <Button
                variant="primary"
                size="md"
                className="flex-1"
                onClick={() => {
                  subscribeToOffer(
                    boxOfferInvitationLink.boxId,
                    boxOffer.id,
                    !boxOffer.priceObject || boxOffer.priceObject.value === 0,
                  )
                    .then(() => {
                      toast.success(word("program.subscribe.toFreeOffer.toast.success"), {
                        classNames: successToastClassName(),
                      })
                      hideSidebar()
                      localStorage.removeItem(LocalStorageKeys.boxOfferInvitation.invitation)
                    })
                    .catch(() => {
                      toast.error(word("program.subscribe.toFreeOffer.toast.error"), {
                        classNames: errorToastClassName(),
                      })
                    })
                }}
                disabled={isProcessing}
              >
                {word("invitationDetailsInfo.accept")}
                {isProcessing ? (
                  <Loader2 color="currentColor" className="animate-spin" width={20} height={20} />
                ) : (
                  <CorrectIcon color="currentColor" width={20} height={20} />
                )}
              </Button>
            </div>
          ) : (
            <div className="relative flex flex-col gap-6 overflow-hidden rounded-4xl bg-neutral-100 p-12">
              <div className="flex items-center justify-center gap-2">
                <WarningIcon color="currentColor" width={24} height={24} className="text-textDefault-body" />
                <Text fontStyle="TL" className="text-textDefault-body">
                  {word("member.offer.invitation.deviceChange.title")}
                </Text>
              </div>
              <Text fontStyle="BM" className="flex justify-center text-textDefault-body">
                {word("member.offer.invitation.deviceChange.description")}
              </Text>
              <div className="flex w-full flex-row items-center justify-center gap-6">
                <Text fontStyle="BM" className="flex w-1/2 justify-center text-textDefault-body">
                  {word("member.offer.invitation.deviceChange.text")}
                </Text>
                <SwitchDeviceIcon color="currentColor" />
              </div>

              <div className="flex items-center justify-center gap-6">
                <div className="flex flex-col gap-2">
                  <Link to={"https://play.google.com/store/apps/details?id=com.healthtech.hustleup"}>
                    <GooglePlayStoreIcon color="currentColor" />
                  </Link>
                  <Link to={"https://apps.apple.com/cm/app/hustle-up/id6450982235"}>
                    <AppleAppStoreIcon color="currentColor" />
                  </Link>
                </div>
                <div className="flex">
                  <QRCodeSVG value={boxOfferInvitationLink.shortLink} size={100} />
                </div>
              </div>
            </div>
          )}

          {/* Card detail content */}
          <div className="relative mb-12 flex flex-col gap-2 overflow-hidden rounded-4xl bg-neutral-100 p-12">
            <Text className="flex border-b border-neutral-200 pb-4 text-textDefault-body" fontStyle="TM">
              {word("box.offer.form.infos.title")}
            </Text>
            {/* Start date & subscription end date */}
            {(boxOffer.invitation.deferredDate !== null || boxOffer.invitation.subscriptionEndDate !== null) && (
              <div className="flex flex-row justify-between gap-2 border-b border-neutral-200 py-3">
                {boxOffer.invitation.deferredDate !== null && (
                  <div className="flex flex-1 flex-col gap-2 py-4">
                    <Text fontStyle="TS" className="text-textDefault-body">
                      {word("box.offer.send.modal.startDate.title")}
                    </Text>
                    <div className="flex items-baseline gap-1">
                      <Text fontStyle="LL" className="text-textDefault-body">
                        {dayjs(boxOffer.invitation.deferredDate).format("L")}
                      </Text>
                      <Text fontStyle="LM" className="text-textDefault-caption">
                        {word("box.offer.send.modal.startDate.inDays", {
                          count: dayjs(boxOffer.invitation.deferredDate).diff(dayjs().startOf("day"), "day"),
                        })}
                      </Text>
                    </div>
                  </div>
                )}
                {boxOffer.invitation.subscriptionEndDate !== null && (
                  <div className="flex flex-1 flex-col gap-2 py-4">
                    <Text fontStyle="TS" className="text-textDefault-body">
                      {word("box.offer.send.modal.endDate.title")}
                    </Text>
                    <div className="flex items-baseline gap-1">
                      <Text fontStyle="LL" className="text-textDefault-caption">
                        {dayjs(boxOffer.invitation.subscriptionEndDate).format("L")}
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Description */}
            <Text
              fontStyle="BM"
              className="line-clamp-2 pt-4 text-textDefault-body"
              style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
              readMore={boxOffer.description.length > 180 ? true : false}
            >
              {boxOffer.description}
            </Text>

            <Separator className="m-0 mt-4 bg-neutral-200" />
            {/* Rules */}
            {boxOffer.type !== BoxOfferType.DropIn && (
              <div className="flex flex-col gap-2 border-b border-neutral-200 py-4">
                <Text fontStyle="TS" className="flex text-textDefault-body">
                  {word("box.offer.form.infos.restrictedPeriod.title")}
                </Text>
                <div className="flex justify-between gap-8">
                  <div className="flex w-full flex-wrap gap-2">
                    {boxOffer.rules && boxOffer.rules.length > 0 ? (
                      boxOffer.rules.map((rule, index) => (
                        <div key={index} className="flex rounded-[1.25rem] border border-neutral-300 px-20 py-16">
                          <Text className="w-full text-center" fontStyle="TS">{`${rule.value} ${word(
                            "offer.form.rules.sessions",
                            { count: rule.value },
                          )} ${word(`offer.form.rules.period.per.${rule.period}`)}`}</Text>
                        </div>
                      ))
                    ) : (
                      <div className="flex w-full rounded-[1.25rem] border border-neutral-300 px-20 py-6">
                        <Text className="w-full text-center" fontStyle="TS">
                          {word("box.offer.form.infos.rules.unlimited")}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Activity included */}
            <div className="flex flex-col gap-2 border-b border-neutral-200 py-4">
              <Text fontStyle="TS" className="text-textDefault-body">
                {word("box.offer.form.infos.activitiesIncluded.title")}
              </Text>
              <div className="mt-2 flex flex-wrap gap-2">
                {boxOffer.programs.map((program) =>
                  program.name.length > 45 ? (
                    <Tooltip key={"tooltip-box-offer-" + boxOffer.id}>
                      <TooltipTrigger>
                        <Toggle
                          key={"toggle-box-offer-" + boxOffer.id}
                          pressed={true}
                          variant="emphasisLow"
                          size="md"
                          className="items-center justify-start text-start"
                        >
                          {shortenText(program.name, 45)}
                        </Toggle>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="w-fit max-w-60 wrap-anywhere">
                        {program.name}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Toggle
                      key={"toggle-box-offer-" + program.id}
                      pressed={true}
                      variant="emphasisLow"
                      size="md"
                      className="items-center justify-start text-start"
                    >
                      {program.name}
                    </Toggle>
                  ),
                )}
              </div>
            </div>

            {/* Commitment period */}
            {boxOffer.minimumCommitmentPeriod && (
              <div className="flex flex-col gap-2 py-4">
                <Text fontStyle="TS" className="text-textDefault-body">
                  {word("box.offer.form.minimum_monthly_duration.label")}
                </Text>
                <div className="flex justify-between gap-2">
                  <Text fontStyle="BM" className="text-textDefault-body">
                    {word("member.offer.invitation.minimumCommitmentPeriod.text")}
                  </Text>
                  <Text fontStyle="BM" className="text-textDefault-body">
                    {boxOffer.minimumCommitmentPeriod}{" "}
                    {word("global.month.label", { count: parseInt(boxOffer.minimumCommitmentPeriod, 10) })}
                  </Text>
                </div>

                {boxOffer.commitmentPeriodAutoRenewal && (
                  <div className="flex justify-between gap-2">
                    <Text fontStyle="BM" className="text-textDefault-body">
                      {word("box.offer.form.infos.commitmentPeriodAutoRenewal.active")}
                    </Text>
                    <Text fontStyle="BM" className="text-textDefault-body">
                      {word("global.yes")}
                    </Text>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
